import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, Brain, ChevronRight } from 'lucide-react';

const TechnologyInActionSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [passwordInput, setPasswordInput] = useState('');
  const [sentimentInput, setSentimentInput] = useState('');
  const [sentimentResult, setSentimentResult] = useState('');

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let score = 0;
    let feedback = [];
    
    if (password.length >= 8) score += 20;
    else feedback.push('Use at least 8 characters');
    
    if (/[a-z]/.test(password)) score += 15;
    else feedback.push('Add lowercase letters');
    
    if (/[A-Z]/.test(password)) score += 15;
    else feedback.push('Add uppercase letters');
    
    if (/\d/.test(password)) score += 15;
    else feedback.push('Add numbers');
    
    if (/[^a-zA-Z\d]/.test(password)) score += 20;
    else feedback.push('Add special characters');
    
    if (password.length >= 12) score += 15;
    
    let strength = 'Very Weak';
    let color = 'bg-red-500';
    
    if (score >= 85) {
      strength = 'Very Strong';
      color = 'bg-green-500';
    } else if (score >= 70) {
      strength = 'Strong';
      color = 'bg-green-400';
    } else if (score >= 50) {
      strength = 'Medium';
      color = 'bg-yellow-500';
    } else if (score >= 25) {
      strength = 'Weak';
      color = 'bg-orange-500';
    }
    
    return { score, strength, color, feedback };
  };

  // Simple sentiment analysis
  const analyzeSentiment = (text) => {
    const positiveWords = ['good', 'great', 'excellent', 'amazing', 'wonderful', 'fantastic', 'awesome', 'love', 'happy', 'brilliant', 'perfect', 'outstanding'];
    const negativeWords = ['bad', 'terrible', 'awful', 'horrible', 'hate', 'angry', 'sad', 'disappointed', 'frustrated', 'disgusting', 'annoying'];
    
    const words = text.toLowerCase().split(/\s+/);
    let positiveCount = 0;
    let negativeCount = 0;
    
    words.forEach(word => {
      if (positiveWords.includes(word.replace(/[^\w]/g, ''))) positiveCount++;
      if (negativeWords.includes(word.replace(/[^\w]/g, ''))) negativeCount++;
    });
    
    let sentiment = 'Neutral';
    let confidence = 50;
    let emoji = '😐';
    let color = 'text-gray-600';
    
    if (positiveCount > negativeCount) {
      sentiment = 'Positive';
      confidence = Math.min(90, 60 + (positiveCount - negativeCount) * 10);
      emoji = '😊';
      color = 'text-green-600';
    } else if (negativeCount > positiveCount) {
      sentiment = 'Negative';
      confidence = Math.min(90, 60 + (negativeCount - positiveCount) * 10);
      emoji = '😞';
      color = 'text-red-600';
    }
    
    return { sentiment, confidence, emoji, color };
  };

  const handlePasswordChange = (e) => {
    setPasswordInput(e.target.value);
  };

  const handleSentimentChange = (e) => {
    const text = e.target.value;
    setSentimentInput(text);
    
    if (text.trim()) {
      const result = analyzeSentiment(text);
      setSentimentResult(`${result.emoji} ${result.sentiment} (${result.confidence}% confidence)`);
    } else {
      setSentimentResult('');
    }
  };

  const passwordStrength = checkPasswordStrength(passwordInput);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section id="technology-demo" className="py-20 md:py-24 bg-white">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            TECHNOLOGY IN ACTION
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            Experience Our Technology
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            Get a glimpse of our capabilities with these interactive demos showcasing our cybersecurity and AI expertise.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto"
        >
          {/* Cybersecurity Demo */}
          <motion.div 
            variants={itemVariants}
            className="bg-lion-gray p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-lion-blue rounded-lg mr-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-poppins font-bold text-lion-blue">Cybersecurity Demo</h3>
                <p className="text-sm text-gray-600">Test the strength of a password in real-time</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="password-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter a password to test:
                </label>
                <input
                  type="text"
                  id="password-input"
                  value={passwordInput}
                  onChange={handlePasswordChange}
                  placeholder="Try a password..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lion-blue focus:border-transparent transition-all duration-300"
                />
              </div>
              
              {passwordInput && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Strength:</span>
                    <span className={`font-medium ${
                      passwordStrength.strength === 'Very Strong' ? 'text-green-600' :
                      passwordStrength.strength === 'Strong' ? 'text-green-500' :
                      passwordStrength.strength === 'Medium' ? 'text-yellow-600' :
                      passwordStrength.strength === 'Weak' ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {passwordStrength.strength}
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-500 ${passwordStrength.color}`}
                      style={{ width: `${passwordStrength.score}%` }}
                    ></div>
                  </div>
                  
                  {passwordStrength.feedback.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-600 mb-2">Suggestions:</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        {passwordStrength.feedback.slice(0, 3).map((tip, index) => (
                          <li key={index} className="flex items-center">
                            <ChevronRight className="w-3 h-3 mr-1 text-lion-yellow" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* AI Sentiment Analysis Demo */}
          <motion.div 
            variants={itemVariants}
            className="bg-lion-gray p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-lion-blue rounded-lg mr-4">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-poppins font-bold text-lion-blue">AI Demo</h3>
                <p className="text-sm text-gray-600">A simple sentiment analysis of your text</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="sentiment-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter some text to analyze:
                </label>
                <textarea
                  id="sentiment-input"
                  rows="4"
                  value={sentimentInput}
                  onChange={handleSentimentChange}
                  placeholder="Type a sentence or paragraph..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lion-blue focus:border-transparent transition-all duration-300 resize-none"
                />
              </div>
              
              {sentimentResult && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Sentiment Analysis Result:</p>
                  <p className="text-lg font-semibold text-center text-lion-blue">
                    {sentimentResult}
                  </p>
                </div>
              )}
              
              <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                <p className="font-medium text-blue-700 mb-1">How it works:</p>
                <p>Our AI analyzes word patterns, context, and sentiment indicators to determine the emotional tone of your text.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mt-12"
        >
          <p className="text-gray-600 mb-6">
            Interested in implementing these technologies in your business?
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                const offset = 80;
                const elementPosition = element.offsetTop - offset;
                window.scrollTo({
                  top: elementPosition,
                  behavior: 'smooth'
                });
              }
            }}
            className="bg-lion-blue text-white font-semibold px-8 py-3 rounded-lg hover:bg-blue-800 transition-colors duration-300 inline-flex items-center group"
          >
            Get Started Today
            <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default TechnologyInActionSection;